import { IPublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { FC, useEffect, useRef, useState } from 'react';
import { Outlet, RouterProvider, createBrowserRouter, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import Layout from './components/layout/Layout';

import initMsalInstance from './features/common/auth/msal-instance';

import { CustomNavigationClient } from './utils/NavigationClient';

import { UserProfile } from './model';
import { AppSettingsContext, useLoadAppSettings, UserProfileContext /*, useLoadAppSettings*/ } from './hooks';
import { UtilsDialogProvider } from 'features/utils/contexts/UtilsDialogContext';
import { createAppRoutes } from 'create-app-routes';
import { EnvironmentType } from 'features/utils/api/models/Environment';
import { Spinner } from 'components/common';
import { FilterContext, FilterContextType } from 'features/utils/contexts/SearchContext';
import { LOCALSTORAGE_CATEGORY_FILTER_KEY, LOCALSTORAGE_ENV_FILTER_KEY } from 'model/common/Constants';
import { UtilCategory } from 'features/utils/api/models/Category';
import Gleap from 'gleap';

export interface SearchContextType {
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
}

export const Application = () => {
  const msalRef = useRef<IPublicClientApplication | null>(null);
  const [msal, setMsal] = useState<IPublicClientApplication | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [environmentTypeFilter, setEnvironmentTypeFilter] = useState<EnvironmentType | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<UtilCategory | null>(null);

  const history = useNavigate();
  const settings = useLoadAppSettings();

  useEffect(() => {
    try {
      const storedEnvFilter = localStorage.getItem(LOCALSTORAGE_ENV_FILTER_KEY);
      if (storedEnvFilter) {
        const parsedEnvFilter = JSON.parse(storedEnvFilter);
        if (
          parsedEnvFilter === null ||
          (typeof parsedEnvFilter === 'number' && Object.values(EnvironmentType).includes(parsedEnvFilter as EnvironmentType))
        ) {
          setEnvironmentTypeFilter(parsedEnvFilter);
        } else {
          console.warn('Invalid environment filter data in localStorage. Resetting.');
          localStorage.removeItem(LOCALSTORAGE_ENV_FILTER_KEY);
        }
      }
    } catch (e) {
      console.error('Failed to load environment type filter from localStorage:', e);
      localStorage.removeItem(LOCALSTORAGE_ENV_FILTER_KEY);
    }

    try {
      const storedCategoryFilter = localStorage.getItem(LOCALSTORAGE_CATEGORY_FILTER_KEY);
      if (storedCategoryFilter) {
        const parsedCategoryFilter = JSON.parse(storedCategoryFilter);
        if (
          parsedCategoryFilter === null ||
          (typeof parsedCategoryFilter === 'number' && Object.values(UtilCategory).includes(parsedCategoryFilter as UtilCategory))
        ) {
          setCategoryFilter(parsedCategoryFilter);
        } else {
          localStorage.removeItem(LOCALSTORAGE_CATEGORY_FILTER_KEY);
        }
      }
    } catch (e) {
      console.error('Failed to load category filter:', e);
      localStorage.removeItem(LOCALSTORAGE_CATEGORY_FILTER_KEY);
    }
  }, []);

  useEffect(() => {
    if (settings && !msalRef.current) {
      initMsalInstance(settings, (account) => {
        if (account) {
          const claims = account.idTokenClaims || {};
          console.log(account);
          const userProfileValue = new UserProfile(
            account.username,
            account.name || '',
            claims?.roles || [],
            claims['extn.utils'] as string[],
            account.idToken || '',
          );
          setUserProfile(userProfileValue);
        }
      }).then((msalInstance) => {
        const navigationClient = new CustomNavigationClient(history);
        msalInstance.setNavigationClient(navigationClient);
        msalRef.current = msalInstance;
        setMsal(msalInstance);
      });
    }
  }, [history, settings]);

  useEffect(() => {
    Gleap.initialize('o083gSNeh3CKC92W1sbKO7FvPO3thehv');
  }, []);

  useEffect(() => {
    if (userProfile) {
      Gleap.identify(userProfile.username, {
        name: userProfile.name,
        email: userProfile.username,
      });
    }
  }, [userProfile]);

  if (!settings || !msal) {
    return <Spinner />;
  }

  const filterContextValue: FilterContextType = {
    searchTerm,
    setSearchTerm,
    environmentTypeFilter,
    setEnvironmentTypeFilter,
    categoryFilter,
    setCategoryFilter,
  };

  return (
    <AppSettingsContext.Provider value={settings}>
      <FilterContext.Provider value={filterContextValue}>
        <MsalProvider instance={msal}>
          <UserProfileContext.Provider value={userProfile}>
            <UtilsDialogProvider>
              <Layout>
                <Outlet />
              </Layout>
              <ToastContainer autoClose={2000} position='bottom-right' />
            </UtilsDialogProvider>
          </UserProfileContext.Provider>
        </MsalProvider>
      </FilterContext.Provider>
    </AppSettingsContext.Provider>
  );
};

const router = createBrowserRouter(createAppRoutes(<Application />));

const App: FC = () => {
  return <RouterProvider router={router} />;
};

export default App;
